'use client';

import dynamic from 'next/dynamic';

import { type CustomBuilderComponentsTypes } from '@wonderful/wwc/dist/src/utils/types';
import { BuilderModelEnums } from '@/helpers/enums';

const TextBox = dynamic(async () => await import('./TextBox/TextBox'));
import { textBoxBuilderConfigs } from './TextBox/TextBox.builder';

const TwoColumnTable = dynamic(
  async () => await import('./TwoColumnTable/TwoColumnTable')
);
import { twoColumnTableBuilderConfigs } from './TwoColumnTable/TwoColumnTable.builder';

const Container = dynamic(async () => await import('./Container/Container'));
import { containerBuilderConfigs } from './Container/Container.builder';

const SectionWithChildren = dynamic(
  async () => await import('./SectionWithChildren/SectionWithChildren')
);
import { sectionWithChildrenBuilderConfigs } from './SectionWithChildren/SectionWithChildren.builder';

const Spacer = dynamic(async () => await import('./Spacer/Spacer'));
import { spacerBuilderConfigs } from './Spacer/Spacer.builder';

const ProductCarousel = dynamic(
  async () => await import('./ProductCarousel/ProductCarousel')
);
import { productCarouselBuilderConfigs } from './ProductCarousel/ProductCarousel.builder';

const ProductListing = dynamic(
  async () => await import('./ProductListing/ProductListing')
);
import { productListingBuilderConfigs } from './ProductListing/ProductListing.builder';

const ProductSizes = dynamic(
  async () => await import('./ProductSizes/ProductSizes')
);
import { productSizesBuilderConfigs } from './ProductSizes/ProductSizes.builder';

const ProductMain = dynamic(
  async () => await import('./ProductMain/ProductMain')
);
import { productMainBuilderConfigs } from './ProductMain/ProductMain.builder';

const RelatedProducts = dynamic(
  async () => await import('./RelatedProducts/RelatedProducts')
);
import { relatedProductsBuilderConfigs } from './RelatedProducts/RelatedProducts.builder';

const FindPistachios = dynamic(
  async () => await import('./FindPistachios/FindPistachios')
);
import { findPistachiosBuilderConfigs } from './FindPistachios/FindPistachios.builder';

const HeadingBlock = dynamic(
  async () => await import('./HeadingBlock/HeadingBlock')
);
import { headingBlockBuilderConfigs } from './HeadingBlock/HeadingBlock.builder';

const Hero = dynamic(async () => await import('./Hero/Hero'));
import { heroBuilderConfigs } from './Hero/Hero.builder';

const FilterableWhereToBuy = dynamic(
  async () => await import('./FilterableWhereToBuy/FilterableWhereToBuy')
);
import { filterableWhereToBuyBuilderConfigs } from './FilterableWhereToBuy/FilterableWhereToBuy.builder';

const FilterableAccordion = dynamic(
  async () => import('./FilterableAccordion/FilterableAccordion')
);
import { filterableAccordionBuilderConfigs } from './FilterableAccordion/FilterableAccordion.builder';

const WhoWeAre = dynamic(async () => import('./WhoWeAre/WhoWeAre'));
import { whoWeAreBuilderConfigs } from './WhoWeAre/WhoWeAre.builder';

const CampaignVimeoModal = dynamic(
  async () => import('./CampaignVimeoModal/CampaignVimeoModal')
);
import { campaignVimeoModalBuilderConfigs } from './CampaignVimeoModal/CampaignVimeoModal.builder';

const VimeoGallery = dynamic(async () => import('./VimeoGallery/VimeoGallery'));
import { vimeoGalleryBuilderConfigs } from './VimeoGallery/VimeoGallery.builder';

const Infographic = dynamic(async () => import('./Infographic/Infographic'));
import { infographicBuilderConfigs } from './Infographic/Infographic.builder';

const Vote = dynamic(async () => import('./Vote/Vote'));
import { voteBuilderConfigs } from './Vote/Vote.builder';

const productPageModels = Object.values(BuilderModelEnums).filter(model =>
  model.startsWith('product-page')
);
const pageLanguageModels = Object.values(BuilderModelEnums).filter(
  model => model.startsWith('page') && !model.includes('product')
);

const generalComponents: CustomBuilderComponentsTypes = {
  name: 'General Components',
  items: [
    {
      component: TextBox,
      ...textBoxBuilderConfigs,
    },
    {
      component: HeadingBlock,
      ...headingBlockBuilderConfigs,
    },
    {
      component: ProductCarousel,
      ...productCarouselBuilderConfigs,
    },
    {
      component: Hero,
      ...heroBuilderConfigs,
    },
    {
      component: ProductListing,
      ...productListingBuilderConfigs,
    },
    {
      component: FilterableAccordion,
      ...filterableAccordionBuilderConfigs,
    },
    {
      component: FilterableWhereToBuy,
      ...filterableWhereToBuyBuilderConfigs,
    },
    {
      component: WhoWeAre,
      ...whoWeAreBuilderConfigs,
    },
    {
      component: FindPistachios,
      ...findPistachiosBuilderConfigs,
    },
    {
      component: CampaignVimeoModal,
      ...campaignVimeoModalBuilderConfigs,
    },
    {
      component: VimeoGallery,
      ...vimeoGalleryBuilderConfigs,
    },
    {
      component: Infographic,
      ...infographicBuilderConfigs,
    },
    {
      component: Vote,
      ...voteBuilderConfigs,
    },
  ],
  allowedModels: pageLanguageModels,
};

const layoutComponents: CustomBuilderComponentsTypes = {
  name: 'Layout Components',
  items: [
    {
      component: SectionWithChildren,
      ...sectionWithChildrenBuilderConfigs,
    },
    {
      component: Spacer,
      ...spacerBuilderConfigs,
    },
    {
      component: Container,
      ...containerBuilderConfigs,
    },
    {
      component: TwoColumnTable,
      ...twoColumnTableBuilderConfigs,
    },
  ],
  allowedModels: [pageLanguageModels, productPageModels].flat(),
};

const productComponents: CustomBuilderComponentsTypes = {
  name: 'Product Components',
  items: [
    {
      component: ProductMain,
      ...productMainBuilderConfigs,
    },
    {
      component: ProductSizes,
      ...productSizesBuilderConfigs,
    },
    {
      component: RelatedProducts,
      ...relatedProductsBuilderConfigs,
    },
  ],
  allowedModels: productPageModels,
};

export const customComponents: CustomBuilderComponentsTypes[] = [
  generalComponents,
  layoutComponents,
  productComponents,
];
